import { Select } from "../../../../../shared/frontend/ui";
import React from "react";
import "./TemplateComboBox.css";
import { KEY_ENTER } from "../../../../../shared/frontend/ui/keyboardKeys";

export type TemplateComboBoxOption = {
  value: string;
  label: React.ReactNode;
  searchText?: string;
  searchLabel?: string;
};

export type TemplateComboBoxOptionGroup = {
  label?: React.ReactNode;
  options: TemplateComboBoxOption[];
};

type TemplateComboBoxProps = {
  className?: string;
  style?: React.CSSProperties;
  onSelect: (value: string) => void;
  onInputConfirm?: (value: string) => void;
  options: TemplateComboBoxOptionGroup[];
  placeholder: string;
  disabled?: boolean;
  notFoundContent?: React.ReactNode;
  ariaLabel?: string;
  onOpen?: () => void;
};

function nodeSearchText(value: React.ReactNode): string {
  if (typeof value === "string" || typeof value === "number") return String(value);
  if (Array.isArray(value)) return value.map(nodeSearchText).join(" ");
  if (React.isValidElement<{ children?: React.ReactNode }>(value)) return nodeSearchText(value.props.children);
  return "";
}

export function TemplateComboBox({
  className,
  style,
  onSelect,
  onInputConfirm,
  options,
  placeholder,
  disabled,
  notFoundContent,
  ariaLabel,
  onOpen
}: TemplateComboBoxProps): React.ReactElement {
  const [resetKey, setResetKey] = React.useState(0);
  const [searchValue, setSearchValue] = React.useState("");
  const selectOptions = React.useMemo(
    () =>
      options.map((group) => ({
        label: group.label,
        options: group.options.map((option) => ({
          ...option,
          searchText: [
            option.searchText,
            option.searchLabel,
            option.value,
            nodeSearchText(option.label),
            nodeSearchText(group.label)
          ]
            .filter(Boolean)
            .join(" ")
        }))
      })),
    [options]
  );

  const stopEditorBubble = (event: React.SyntheticEvent) => {
    event.stopPropagation();
  };

  return (
    <div
      className={`template-combobox${className ? ` ${className}` : ""}`}
      style={style}
      onChange={stopEditorBubble}
      onClick={stopEditorBubble}
      onInput={stopEditorBubble}
      onKeyDown={stopEditorBubble}
      onMouseDown={stopEditorBubble}
      onPointerDown={stopEditorBubble}
    >
      <Select
        key={resetKey}
        style={{ width: "100%" }}
        searchValue={searchValue}
        showSearch
        disabled={disabled}
        placeholder={placeholder}
        options={selectOptions}
        notFoundContent={notFoundContent}
        aria-label={ariaLabel || placeholder}
        optionFilterProp="searchText"
        filterOption={(input, option) =>
          String((option as { searchText?: unknown } | undefined)?.searchText ?? "")
            .toLowerCase()
            .includes(input.trim().toLowerCase())
        }
        onSearch={setSearchValue}
        onInputKeyDown={(event) => {
          if (event.key !== KEY_ENTER || !onInputConfirm) return;
          const nextValue = searchValue.trim();
          if (!nextValue) return;
          event.preventDefault();
          onInputConfirm(nextValue);
          setSearchValue("");
          setResetKey((current) => current + 1);
        }}
        onOpenChange={(nextOpen: boolean) => {
          if (nextOpen) onOpen?.();
        }}
        onSelect={(nextValue) => {
          onSelect(String(nextValue));
          setSearchValue("");
          setResetKey((current) => current + 1);
        }}
      />
    </div>
  );
}
